import { useState, useCallback } from 'react';

export const useFormValidation = (formData, user_role, iamUserList) => {
  const [errors, setErrors] = useState({
    firstName: '',
    lastName: '',
    email: '',
    org: '',
    advertiserAccess: '',
    message: ''
  });

  // Validate email format and domain
  const validateEmail = useCallback((email) => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!re.test(String(email).toLowerCase())) {
      return { isValid: false, message: 'Invalid email address' };
    }
    if (!email.toLowerCase().endsWith('@inmobi.com')) {
      return { isValid: false, message: 'Email must be @inmobi.com domain' };
    }
    return { isValid: true, message: '' };
  }, []);

  // Check if email already exists in IAM
  const checkEmailExist = useCallback(
    (email) => {
      if (!iamUserList || !Array.isArray(iamUserList)) {
        return false;
      }
      return iamUserList.some((iamUser) => iamUser.email === email);
    },
    [iamUserList]
  );

  // Validate individual field
  const validateField = useCallback(
    (name, value) => {
      let error = '';

      if ((name === 'firstName' || name === 'lastName') && value.trim() === '') {
        error = `${name === 'firstName' ? 'First Name' : 'Last Name'} is required`;
      } else if (name === 'email') {
        const emailValidation = validateEmail(value);
        if (!emailValidation.isValid) {
          error = emailValidation.message;
        } else if (checkEmailExist(value)) {
          error = 'Email Already Exist ON IAM';
        }
      } else if (name === 'org' && user_role === 'admin' && value.trim() === '') {
        error = 'Organization is required';
      } else if (name === 'advertiserAccess' && value.trim() === '') {
        error = 'Advertiser Access is required';
      }

      setErrors((prevErrors) => ({
        ...prevErrors,
        [name]: error
      }));
    },
    [validateEmail, checkEmailExist, user_role]
  );

  // Check if form is valid
  const isFormValid = useCallback(() => {
    const basicValidation =
      formData.firstName.trim() !== '' &&
      formData.lastName.trim() !== '' &&
      formData.email.trim() !== '' &&
      formData.advertiserAccess.trim() !== '' &&
      !errors.firstName &&
      !errors.lastName &&
      !errors.email &&
      !errors.advertiserAccess &&
      (user_role === 'admin' ? formData.org.trim() !== '' && !errors.org : true);

    // Additional security check: Non-admin users cannot create admin users
    const adminValidation = !(formData.isAdmin && user_role !== 'admin');

    return basicValidation && adminValidation;
  }, [formData, errors, user_role]);

  // Clear specific error
  const clearError = useCallback((fieldName) => {
    setErrors((prevErrors) => ({
      ...prevErrors,
      [fieldName]: ''
    }));
  }, []);

  // Clear all errors
  const clearAllErrors = useCallback(() => {
    setErrors({
      firstName: '',
      lastName: '',
      email: '',
      org: '',
      advertiserAccess: '',
      message: ''
    });
  }, []);

  return {
    errors,
    validateField,
    isFormValid,
    clearError,
    clearAllErrors
  };
};
